// api.js
import axios from "axios";
import { API_URL } from './config';
import { isBackendOutageError, isServerErrorRoute } from './utils/serverHealth';
import { captureClientError } from './utils/errorReporting';
import SockJS from "sockjs-client";
//import { over } from "stompjs";
import { Client } from "@stomp/stompjs";

let stompClient = null;

const MAX_REDIRECTS = 1;

const getRedirectAttempts = () => {
  try {
    return parseInt(sessionStorage.getItem('redirectAttempts') || '0', 10);
  } catch {
    return 0;
  }
};

const incrementRedirectAttempts = () => {
  try {
    const current = getRedirectAttempts();
    sessionStorage.setItem('redirectAttempts', (current + 1).toString());
  } catch { }
};

export const resetRedirectAttempts = () => {
  try {
    sessionStorage.removeItem('redirectAttempts');
  } catch { }
};

const redirectTo = (path) => {
  if (window.location.pathname === path) return;

  const attempts = getRedirectAttempts();
  if (attempts >= MAX_REDIRECTS) {
    console.error(`Max redirect attempts (${MAX_REDIRECTS}) reached. Stopping redirect to ${path} to prevent loop.`);
    return;
  }

  incrementRedirectAttempts();
  window.location.replace(path);
};

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  // config.headers['X-Client-Platform'] = 'WEB';
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // On any successful response, reset redirect attempts
    resetRedirectAttempts();
    return response;
  },
  (error) => {
    // Check if the error is due to network/server unreachability
    if (isBackendOutageError(error)) {
      error.isBackendOutage = true;

      // Differentiate between timeout and connection issues
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNetworkError = error.message?.includes('Network Error') || error.code === 'ERR_NETWORK';

      const errorPayload = {
        message: error.message || 'Unknown network error',
        code: error.code || 'UNKNOWN_ERROR',
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method?.toUpperCase(),
        params: error.config?.params,
        status: error.response?.status || null,
        statusText: error.response?.statusText || null,
        data: error.response?.data || null,
        isTimeout,
        isNetworkError,
        page: window.location.pathname
      };

      console.group('🚨 [API Outage / Network Error]');
      console.error('Error Details:', errorPayload);
      console.error('Raw Error Object:', error);
      console.groupEnd();

      captureClientError(error, {
        source: 'api-response-interceptor',
        component: error.config?.url || 'AxiosRequest',
        ...errorPayload
      });

      // Prevent redirect loop if already on an error page
      if (isServerErrorRoute()) {
        return Promise.reject(error);
      }

      if (isTimeout) {
        redirectTo("/timeout");
      } else if (isNetworkError) {
        redirectTo("/connection-error");
      } else {
        redirectTo("/server-down");
      }

      return Promise.reject(error);
    }
    const status = error.response?.status;

    console.log('Interceptor caught error:', {
      status,
      data: error.response?.data,
    });

    // --- 401 Unauthorized (classic expired token)
    if (status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      return;
    }

    // --- 403 Forbidden (check if token expired disguised as 403)
    if (status === 403) {
      const errorMessage = error.response.data?.error || error.response.data?.message;

      if (errorMessage && /expired|invalid/i.test(errorMessage)) {
        // Token actually expired
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      } else {
        // Real permission issue
        //alert("You may be trying to access a section you don't have permission for.");
        console.warn("Access forbidden: ", errorMessage);
        console.warn("You may be trying to access a section you don't have permission for.");
        //return Promise.reject(error);
        // Real permission issue during data fetching:
        // → Return a "fake" successful response with empty data
        // so your components don't crash
        return Promise.resolve({ data: [], forbidden: true });

      }
    }

    // --- 429 Too Many Requests (Rate limit retry logic)
    if (status === 429) {
      const config = error.config || {};
      config._retryCount = config._retryCount || 0;

      if (config._retryCount < 3) {
        config._retryCount += 1;

        const headerRetryAfter = error.response?.headers?.['retry-after'];
        const bodyRetryAfter = error.response?.data?.retryAfterSeconds;
        let delayMs = 1000 * Math.pow(2, config._retryCount - 1);

        if (headerRetryAfter) {
          delayMs = Math.max(parseInt(headerRetryAfter, 10) * 1000, delayMs);
        } else if (bodyRetryAfter) {
          delayMs = Math.max(parseInt(bodyRetryAfter, 10) * 1000, delayMs);
        }

        console.warn(`Rate limit (429) hit. Retrying request (${config._retryCount}/3) in ${delayMs}ms...`, config.url);

        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(api(config));
          }, delayMs);
        });
      }
    }

    // Let other errors bubble up
    return Promise.reject(error);
  }
);

const wsListeners = new Set();

function ensureStompClient() {
  if (stompClient && (stompClient.active || stompClient.connected)) {
    return stompClient;
  }

  const normalizedApiUrl = String(API_URL || '').replace(/\/$/, '');
  const wsEndpoint = normalizedApiUrl.endsWith('/api')
    ? `${normalizedApiUrl}/ws`
    : `${normalizedApiUrl}/api/ws`;

  const token = localStorage.getItem("token");

  stompClient = new Client({
    webSocketFactory: () => new SockJS(wsEndpoint),
    connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
    reconnectDelay: 5000,
    debug: (str) => {
      // debug logs
    },
    onConnect: () => {
      console.log("✅ WebSocket connected to", wsEndpoint);
      const topics = ["leaves", "absence", "requests", "notifications"];
      topics.forEach(topic => {
        stompClient.subscribe(`/topic/${topic}`, (message) => {
          try {
            const data = JSON.parse(message.body);
            wsListeners.forEach(listener => {
              try {
                listener(topic, data);
              } catch (e) {
                console.error("Error in WebSocket listener:", e);
              }
            });
          } catch (e) {
            console.warn("Could not parse STOMP message:", e);
          }
        });
      });
    },
    onStompError: (frame) => {
      console.warn("STOMP error:", frame?.headers?.message || frame);
    },
    onWebSocketError: (error) => {
      console.warn("WebSocket error:", error);
    },
    onDisconnect: () => {
      console.log("🔌 STOMP disconnected");
    }
  });

  stompClient.activate();
  return stompClient;
}

export function connectWebSocket(onMessage) {
  if (typeof onMessage === 'function') {
    wsListeners.add(onMessage);
  }
  ensureStompClient();

  return () => {
    if (typeof onMessage === 'function') {
      wsListeners.delete(onMessage);
    }
  };
}

export function disconnectWebSocket() {
  wsListeners.clear();
  if (stompClient) {
    stompClient.deactivate();
    stompClient = null;
    console.log("🔌 WebSocket manually disconnected");
  }
}

export default api;
